import { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  fetchDashboardKPI,
  fetchDashboardSales,
  fetchDashboardLeads,
  fetchDashboardPropertyPerformance,
} from '../../services/dashboardAnalyticsService';
import '../../styles/dashboard-analytics.css';
import AnalyticsKPICards from './AnalyticsKPICards';
import AnalyticsKPICardsSkeleton from './AnalyticsKPICardsSkeleton';
import SalesAnalytics from './SalesAnalytics';
import SalesAnalyticsSkeleton from './SalesAnalyticsSkeleton';
import LeadAnalytics from './LeadAnalytics';
import LeadAnalyticsSkeleton from './LeadAnalyticsSkeleton';
import PropertyPerformance from './PropertyPerformance';
import PropertyPerformanceSkeleton from './PropertyPerformanceSkeleton';

const getDefaultDateFrom = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDefaultDateTo = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const OwnerDashboard = () => {
  const [kpi, setKpi] = useState(null);
  const [sales, setSales] = useState(null);
  const [leads, setLeads] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [salesPeriod, setSalesPeriod] = useState('monthly');
  const [salesGroup, setSalesGroup] = useState('period');
  const [salesLevel, setSalesLevel] = useState('province');

  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
  const [dateTo, setDateTo] = useState(getDefaultDateTo);

  const dateParams = useMemo(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom.toISOString().split('T')[0];
    if (dateTo) params.date_to = dateTo.toISOString().split('T')[0];
    return params;
  }, [dateFrom, dateTo]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const salesParams = {
        ...dateParams,
        period: salesPeriod,
        group: salesGroup,
        level: salesLevel,
      };

      const [kpiData, salesData, leadsData, perfData] = await Promise.all([
        fetchDashboardKPI(dateParams),
        fetchDashboardSales(salesParams),
        fetchDashboardLeads(dateParams),
        fetchDashboardPropertyPerformance(dateParams),
      ]);

      setKpi(kpiData);
      setSales(salesData);
      setLeads(leadsData);
      setPerformance(perfData);
    } catch (err) {
      setError(err?.response?.data?.message || 'Gagal memuat data dashboard.');
    } finally {
      setLoading(false);
    }
  }, [dateParams, salesPeriod, salesGroup, salesLevel]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">{error}</div>
        <button
          onClick={fetchAll}
          style={{
            padding: '8px 20px',
            background: '#0b2464',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontWeight: 700,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-page__header">
        <h2 className="analytics-page__title">Dashboard Analytics</h2>
        <div className="analytics-page__date-filter">
          <DatePicker
            selected={dateFrom}
            onChange={(d) => setDateFrom(d || getDefaultDateFrom())}
            selectsStart
            startDate={dateFrom}
            endDate={dateTo}
            dateFormat="yyyy-MM-dd"
            className="analytics-page__date-input"
            placeholderText="Dari tanggal"
            isClearable
          />
          <DatePicker
            selected={dateTo}
            onChange={(d) => setDateTo(d || getDefaultDateTo())}
            selectsEnd
            startDate={dateFrom}
            endDate={dateTo}
            minDate={dateFrom}
            dateFormat="yyyy-MM-dd"
            className="analytics-page__date-input"
            placeholderText="Sampai tanggal"
            isClearable
          />
        </div>
      </div>

      {loading ? (
        <>
          <AnalyticsKPICardsSkeleton />
          <div className="analytics-grid">
            <SalesAnalyticsSkeleton />
            <LeadAnalyticsSkeleton />
          </div>
          <PropertyPerformanceSkeleton />
        </>
      ) : (
        <>
          <AnalyticsKPICards kpi={kpi} />

          <div className="analytics-grid">
            <SalesAnalytics
              data={sales}
              period={salesPeriod}
              group={salesGroup}
              level={salesLevel}
              onPeriodChange={setSalesPeriod}
              onGroupChange={(g) => { setSalesGroup(g); }}
              onLevelChange={(l) => { setSalesLevel(l); }}
            />
            <LeadAnalytics data={leads} />
          </div>

          <div className="analytics-grid analytics-grid--full">
            <PropertyPerformance data={performance} />
          </div>
        </>
      )}
    </div>
  );
};

export default OwnerDashboard;
